import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';

const TABLE_NAME = 'rd_cco';

const EMPTY_FORM = {
  data_intervento: '',
  data_rientro: '',
  tipointervento: '',
  colata: '',
  n_macchina: '',
  linea: '',
  note: '',
  snrientrato: '',
  posizione: '',
  stato_rid: '',
  snsmontato: '',
  snmontato: '',
  causa_freno: false,
};

const SELECT_FIELDS = [
  'tipointervento',
  'n_macchina',
  'linea',
  'colata',
  'posizione',
  'stato',
  'stato_rid',
];

const toIsoOrNull = value => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

const safeString = value => String(value ?? '');

const uniqueValues = values => [...new Set(values.map(value => safeString(value).trim()).filter(Boolean))]
  .sort((a, b) => a.localeCompare(b, 'it', { numeric: true, sensitivity: 'base' }));

// ─── Toast ────────────────────────────────────────────────────────────────────
const Toast = ({ notification, onDismiss }) => {
  if (!notification) return null;
  const success = notification.type === 'success';
  const warning = notification.type === 'warning';
  const background = success ? 'bg-emerald-500' : warning ? 'bg-amber-500' : 'bg-red-500';
  const icon = success ? 'CheckCircle' : warning ? 'AlertTriangle' : 'AlertCircle';

  return (
    <div role="status" aria-live="polite" className={`fixed top-6 left-1/2 -translate-x-1/2 z-[220] px-5 py-3.5 rounded-2xl shadow-2xl flex items-center gap-3 ${background} text-white max-w-[92vw] animate-in slide-in-from-top-4 duration-300`}>
      <Icon name={icon} size={18} className="shrink-0" />
      <span className="text-sm font-black uppercase tracking-wider">{notification.message}</span>
      <button type="button" onClick={onDismiss} aria-label="Chiudi notifica" className="ml-2 shrink-0 w-5 h-5 rounded-full bg-white/20 hover:bg-white/40 flex items-center justify-center transition-all"><Icon name="X" size={11} /></button>
    </div>
  );
};

// ─── Conferma chiusura ─────────────────────────────────────────────────────────
const ConfirmDialog = ({ open, onConfirm, onCancel }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[230] flex items-center justify-center p-4" role="alertdialog" aria-modal="true" aria-labelledby="confirm-close-title">
      <button type="button" aria-label="Chiudi conferma" onClick={onCancel} className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div className="relative w-full max-w-sm bg-card/90 backdrop-blur-xl border border-border/60 rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
        <div className="h-1 bg-gradient-to-r from-red-500 via-primary/60 to-transparent" />
        <div className="p-7 flex flex-col gap-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-red-500/10 text-red-500 flex items-center justify-center shrink-0"><Icon name="AlertTriangle" size={20} /></div>
            <div>
              <h3 id="confirm-close-title" className="text-base font-black uppercase tracking-tight">Scartare i dati inseriti?</h3>
              <p className="mt-1 text-xs font-bold text-muted-foreground/60">Il nuovo intervento non verrà salvato.</p>
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onCancel} className="flex-1 px-4 py-2.5 rounded-2xl bg-muted hover:bg-muted/80 border border-border/40 text-foreground text-[11px] font-black uppercase tracking-widest transition-all">Continua modifica</button>
            <button type="button" onClick={onConfirm} className="flex-1 px-4 py-2.5 rounded-2xl bg-red-500 hover:bg-red-600 text-white text-[11px] font-black uppercase tracking-widest transition-all">Esci senza salvare</button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Card di sezione (glassmorphism) ───────────────────────────────────────────
const FormCard = ({ title, icon, description, gridClassName = 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3', children }) => (
  <section className="rounded-3xl border border-border/40 bg-card/40 backdrop-blur-md shadow-xl shadow-black/5 p-5 sm:p-6 lg:p-7 flex flex-col gap-5">
    <header className="flex items-start gap-3">
      <div className="w-9 h-9 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary shrink-0">
        <Icon name={icon} size={16} />
      </div>
      <div className="flex flex-col">
        <h3 className="text-[11px] font-black uppercase tracking-[.25em] text-primary/80">{title}</h3>
        {description && <p className="text-[10px] font-bold text-muted-foreground/50 mt-0.5">{description}</p>}
      </div>
    </header>
    <div className={`grid ${gridClassName} gap-5`}>
      {children}
    </div>
  </section>
);

// ─── Campo di testo / textarea ──────────────────────────────────────────────────
const TextField = ({ label, name, value, onChange, placeholder, error, required, disabled, type = 'text', icon, sublabel, rows, inputRef }) => {
  const errorId = `${name}-error`;
  return (
    <div className="flex flex-col gap-1.5 w-full group">
      <div className="flex items-center justify-between px-1">
        <label htmlFor={name} className={`text-[10px] font-black uppercase tracking-[.15em] select-none transition-colors ${error ? 'text-red-500' : 'text-muted-foreground/60 group-focus-within:text-primary'}`}>
          {label}{required && <span className="text-primary ml-0.5" aria-hidden="true">*</span>}
          {sublabel && <span className="ml-1.5 text-muted-foreground/30 normal-case font-bold text-[9px] tracking-normal">{sublabel}</span>}
        </label>
        {error && <span id={errorId} role="alert" className="text-[9px] font-black text-red-500 uppercase tracking-widest flex items-center gap-1"><Icon name="AlertCircle" size={9} />{error}</span>}
      </div>
      <div className="relative">
        {icon && <Icon name={icon} size={14} className="absolute left-3.5 top-3.5 text-primary/40 pointer-events-none transition-colors group-focus-within:text-primary" />}
        {rows ? (
          <textarea
            id={name}
            name={name}
            rows={rows}
            value={value ?? ''}
            onChange={onChange}
            placeholder={placeholder}
            disabled={disabled}
            aria-invalid={!!error}
            aria-describedby={error ? errorId : undefined}
            className={`w-full rounded-2xl px-4 py-3 text-sm font-bold outline-none transition-all resize-none backdrop-blur-md ${icon ? 'pl-10' : ''} ${error ? 'bg-red-500/5 border-2 border-red-500/40 focus:ring-4 focus:ring-red-500/10' : 'bg-muted/30 border border-border/40 focus:border-primary/50 focus:bg-background/80 focus:ring-4 focus:ring-primary/10 hover:border-border/70'} ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
          />
        ) : (
          <input
            ref={inputRef}
            id={name}
            name={name}
            type={type}
            value={value ?? ''}
            onChange={onChange}
            placeholder={placeholder}
            disabled={disabled}
            aria-invalid={!!error}
            aria-describedby={error ? errorId : undefined}
            className={`w-full rounded-2xl px-4 py-3 text-sm font-bold outline-none transition-all backdrop-blur-md ${icon ? 'pl-10' : ''} ${error ? 'bg-red-500/5 border-2 border-red-500/40 focus:ring-4 focus:ring-red-500/10' : 'bg-muted/30 border border-border/40 focus:border-primary/50 focus:bg-background/80 focus:ring-4 focus:ring-primary/10 hover:border-border/70'} ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
          />
        )}
      </div>
    </div>
  );
};

// ─── Campo con suggerimenti (datalist) ─────────────────────────────────────────
const ComboField = ({ label, name, value, onChange, options, placeholder, error, required, disabled, icon, sublabel }) => {
  const listId = `options-${name}`;
  const errorId = `${name}-error`;
  return (
    <div className="flex flex-col gap-1.5 w-full group">
      <div className="flex items-center justify-between px-1">
        <label htmlFor={name} className={`text-[10px] font-black uppercase tracking-[.15em] select-none transition-colors ${error ? 'text-red-500' : 'text-muted-foreground/60 group-focus-within:text-primary'}`}>
          {label}{required && <span className="text-primary ml-0.5" aria-hidden="true">*</span>}
          {sublabel && <span className="ml-1.5 text-muted-foreground/30 normal-case font-bold text-[9px] tracking-normal">{sublabel}</span>}
        </label>
        {error && <span id={errorId} role="alert" className="text-[9px] font-black text-red-500 uppercase tracking-widest flex items-center gap-1"><Icon name="AlertCircle" size={9} />{error}</span>}
      </div>
      <div className="relative">
        <Icon name={icon || 'List'} size={14} className="absolute left-3.5 top-3.5 text-primary/40 pointer-events-none transition-colors group-focus-within:text-primary" />
        <input
          id={name}
          name={name}
          type="text"
          list={listId}
          value={value ?? ''}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          aria-invalid={!!error}
          aria-describedby={error ? errorId : undefined}
          className={`w-full rounded-2xl py-3 pl-10 pr-4 text-sm font-bold outline-none transition-all backdrop-blur-md ${error ? 'bg-red-500/5 border-2 border-red-500/40 focus:ring-4 focus:ring-red-500/10' : 'bg-muted/30 border border-border/40 focus:border-primary/50 focus:bg-background/80 focus:ring-4 focus:ring-primary/10 hover:border-border/70'} ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
        />
        {options?.length > 0 && <datalist id={listId}>{options.map(option => <option key={option} value={option} />)}</datalist>}
      </div>
    </div>
  );
};

// ─── Switch premium per Causa Freno ────────────────────────────────────────────
const BooleanField = ({ value, onChange }) => {
  const toggle = () => onChange({ target: { name: 'causa_freno', value: !value } });
  return (
    <div className="flex flex-col gap-1.5 w-full group">
      <label className="px-1 text-[10px] font-black uppercase tracking-[.15em] text-muted-foreground/60 group-focus-within:text-primary">
        Causa Freno
      </label>
      <button
        type="button"
        role="switch"
        aria-checked={value}
        onClick={toggle}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } }}
        className={`relative flex items-center justify-between w-full rounded-2xl px-4 py-3.5 border-2 backdrop-blur-md transition-all duration-300 focus:outline-none focus:ring-4 ${
          value
            ? 'bg-red-500/10 border-red-500/40 text-red-500 focus:ring-red-500/20 shadow-[0_0_20px_-4px_rgba(239,68,68,0.35)]'
            : 'bg-emerald-500/10 border-emerald-500/40 text-emerald-600 focus:ring-emerald-500/20 shadow-[0_0_20px_-4px_rgba(16,185,129,0.25)]'
        }`}
      >
        <span className="flex items-center gap-2.5">
          <Icon name={value ? 'AlertTriangle' : 'CheckCircle2'} size={17} className={value ? 'animate-pulse' : ''} />
          <span className="text-sm font-black tracking-tight">
            {value ? 'SI — Causa freno presente' : 'NO — Nessuna causa freno'}
          </span>
        </span>
        <span className={`relative w-11 h-6 rounded-full transition-colors duration-300 shrink-0 ${value ? 'bg-red-500' : 'bg-emerald-500'}`}>
          <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-md transition-all duration-300 ${value ? 'left-5' : 'left-0.5'}`} />
        </span>
      </button>
      <span className="px-1 text-[9px] font-bold text-muted-foreground/40">
        Nel database verrà salvato come valore booleano: {value ? 'true' : 'false'}.
      </span>
    </div>
  );
};

const NewInsertRaddrizzatrice = ({ isOpen, onClose, onRefresh }) => {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [options, setOptions] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const [confirmClose, setConfirmClose] = useState(false);

  const modalRef = useRef(null);
  const firstFieldRef = useRef(null);

  const fetchOptions = useCallback(async () => {
    setOptionsLoading(true);
    try {
      const result = {};
      await Promise.all(SELECT_FIELDS.map(async field => {
        const { data, error } = await supabase.from(TABLE_NAME).select(field).not(field, 'is', null);
        if (error) throw error;
        result[field] = uniqueValues((data || []).map(row => row[field]));
      }));
      setOptions(result);
    } catch (error) {
      console.error('Errore caricamento opzioni rd_cco:', error);
      setNotification({ type: 'warning', message: 'Impossibile caricare tutti i valori esistenti.' });
    } finally {
      setOptionsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;
    fetchOptions();
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, [isOpen, fetchOptions]);

  // ── Autofocus sul primo campo all'apertura ──────────────────────────────────
  useEffect(() => {
    if (!isOpen) return undefined;
    const t = window.setTimeout(() => firstFieldRef.current?.focus(), 60);
    return () => window.clearTimeout(t);
  }, [isOpen]);

  // ── Focus trap: Tab/Shift+Tab restano dentro il modal ────────────────────────
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleTab = (event) => {
      if (event.key !== 'Tab' || !modalRef.current) return;
      const focusable = modalRef.current.querySelectorAll('input, textarea, button, select, [tabindex]:not([tabindex="-1"])');
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', handleTab);
    return () => window.removeEventListener('keydown', handleTab);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handler = event => {
      if (event.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  });

  useEffect(() => {
    if (!notification) return undefined;
    const timer = window.setTimeout(() => setNotification(null), 5000);
    return () => window.clearTimeout(timer);
  }, [notification]);

  const isDirty = useMemo(() => Object.entries(formData).some(([key, value]) => key === 'causa_freno' ? value === true : value !== '' && value !== null), [formData]);

  const handleChange = event => {
    const { name, value } = event.target;
    setFormData(previous => ({ ...previous, [name]: value }));
    if (errors[name]) setErrors(previous => ({ ...previous, [name]: null }));
  };

  const resetForm = () => {
    setFormData(EMPTY_FORM);
    setErrors({});
    setConfirmClose(false);
  };

  const handleClose = () => {
    if (loading) return;
    if (isDirty) setConfirmClose(true);
    else { resetForm(); onClose?.(); }
  };

  const validate = () => {
    const nextErrors = {};
    if (!formData.data_intervento) nextErrors.data_intervento = 'Obbligatorio';
    if (!safeString(formData.tipointervento).trim()) nextErrors.tipointervento = 'Obbligatorio';
    if (!safeString(formData.n_macchina).trim()) nextErrors.n_macchina = 'Obbligatorio';
    if (!safeString(formData.linea).trim()) nextErrors.linea = 'Obbligatorio';
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) setNotification({ type: 'error', message: 'Compila tutti i campi obbligatori.' });
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async event => {
    event.preventDefault();
    if (!validate()) return;
    setLoading(true);
    setNotification(null);

    try {
      const payload = {
        data_intervento: toIsoOrNull(formData.data_intervento),
        data_rientro: toIsoOrNull(formData.data_rientro),
        tipointervento: safeString(formData.tipointervento).trim() || null,
        colata: safeString(formData.colata).trim() || null,
        n_macchina: safeString(formData.n_macchina).trim() || null,
        linea: safeString(formData.linea).trim() || null,
        note: safeString(formData.note).trim() || null,
        snrientrato: safeString(formData.snrientrato).trim() || null,
        posizione: safeString(formData.posizione).trim() || null,
        stato_rid: safeString(formData.stato_rid).trim() || null,
        snsmontato: safeString(formData.snsmontato).trim() || null,
        snmontato: safeString(formData.snmontato).trim() || null,
        causa_freno: Boolean(formData.causa_freno),
      };

      const { error } = await supabase.from(TABLE_NAME).insert(payload);
      if (error) throw error;

      setNotification({ type: 'success', message: 'Intervento salvato correttamente.' });
      onRefresh?.();
      resetForm();
      onClose?.();
    } catch (error) {
      console.error('Errore inserimento rd_cco:', error);
      setNotification({ type: 'error', message: error?.message || 'Errore durante il salvataggio.' });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return <Toast notification={notification} onDismiss={() => setNotification(null)} />;

  const tipoOptions = options.tipointervento || [];
  const macchinaOptions = options.n_macchina || [];
  const lineaOptions = options.linea || [];
  const colataOptions = options.colata || [];
  const posizioneOptions = options.posizione || [];
  const statoOptions = options.stato || [];
  const statoRidOptions = options.stato_rid || [];

  const campiCompilati = Object.values(formData).filter(value => value !== '' && value !== false && value !== null).length;

  return (
    <>
      <Toast notification={notification} onDismiss={() => setNotification(null)} />
      <ConfirmDialog open={confirmClose} onCancel={() => setConfirmClose(false)} onConfirm={() => { resetForm(); onClose?.(); }} />
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-6 overflow-hidden">
        <button type="button" aria-label="Chiudi nuovo intervento" onClick={handleClose} className="absolute inset-0 bg-background/80 backdrop-blur-xl animate-in fade-in duration-300" />

        <div
          ref={modalRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="new-intervento-title"
          className="relative w-full max-w-4xl bg-card/80 backdrop-blur-xl border border-border/50 rounded-[2.5rem] shadow-2xl overflow-hidden flex flex-col max-h-[94vh] animate-in zoom-in-95 duration-300"
        >
          <div className="absolute -top-20 -right-20 w-56 h-56 bg-primary/10 blur-[70px] rounded-full pointer-events-none" />

          {/* Header */}
          <div className="shrink-0 px-6 md:px-10 py-5 md:py-7 flex items-center justify-between border-b border-border/40 relative z-10 bg-card/60 backdrop-blur-sm">
            <div className="flex items-center gap-3 md:gap-4">
              <div className="w-10 h-10 md:w-11 md:h-11 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary shadow-sm">
                <Icon name="PlusCircle" size={20} />
              </div>
              <div>
                <h2 id="new-intervento-title" className="text-lg md:text-xl font-black tracking-tighter uppercase text-foreground">Nuovo Intervento</h2>
                <p className="text-[9px] font-black uppercase tracking-[.3em] text-muted-foreground/50 mt-0.5">
                  Gestione Raddrizzatrici CCO <span className="ml-2 text-primary/50">{optionsLoading ? 'Caricamento valori...' : 'Valori pronti'}</span>
                </p>
              </div>
            </div>
            <button type="button" onClick={handleClose} aria-label="Chiudi" className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-2xl bg-muted/50 hover:bg-red-500/10 hover:text-red-500 border border-border/40 hover:border-red-500/20 text-muted-foreground transition-all">
              <Icon name="X" size={16} />
            </button>
          </div>

          {/* Form */}
          <form
            id="raddrizzatrice-form"
            onSubmit={handleSubmit}
            noValidate
            className="flex-1 overflow-y-auto px-5 md:px-10 py-6 md:py-8 relative z-10"
            style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(128,128,128,0.15) transparent' }}
          >
            <div className="flex flex-col gap-6">

              <FormCard title="Quando e Identificazione" icon="Clock" gridClassName="grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
                <TextField inputRef={firstFieldRef} label="Data Intervento" name="data_intervento" type="datetime-local" value={formData.data_intervento} onChange={handleChange} required error={errors.data_intervento} icon="Calendar" />
                <ComboField label="Tipo Intervento" name="tipointervento" value={formData.tipointervento} onChange={handleChange} options={tipoOptions.length ? tipoOptions : ['MONTATO', 'SMONTATO', 'RIPARAZIONE', 'ALTRO']} placeholder="MONTATO, SMONTATO..." required error={errors.tipointervento} icon="Wrench" />
                <ComboField label="N° Macchina" name="n_macchina" value={formData.n_macchina} onChange={handleChange} options={macchinaOptions} placeholder="Inserisci o seleziona macchina" required error={errors.n_macchina} icon="Settings" />
                <ComboField label="Linea" name="linea" value={formData.linea} onChange={handleChange} options={lineaOptions} placeholder="Inserisci o seleziona linea" required error={errors.linea} icon="GitBranch" />
              </FormCard>

              <FormCard title="Posizione e Stato" icon="MapPin" description="Dove si trova l'intervento e a che punto è" gridClassName="grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                <ComboField label="Colata" name="colata" value={formData.colata} onChange={handleChange} options={colataOptions} placeholder="Inserisci o seleziona colata" icon="Layers" />
                <ComboField label="Posizione" name="posizione" value={formData.posizione} onChange={handleChange} options={posizioneOptions} placeholder="Inserisci o seleziona posizione" icon="MapPin" />
                <ComboField label="Stato" name="stato" value={formData.stato} onChange={handleChange} options={statoOptions} placeholder="Inserisci o seleziona stato" icon="Activity" />
                <ComboField label="Stato RID" name="stato_rid" value={formData.stato_rid} onChange={handleChange} options={statoRidOptions} placeholder="Inserisci o seleziona stato RID" icon="ShieldCheck" />
                <TextField label="Data Rientro" name="data_rientro" type="datetime-local" value={formData.data_rientro} onChange={handleChange} icon="CalendarCheck" sublabel="opzionale" />
                <BooleanField value={formData.causa_freno} onChange={handleChange} />
              </FormCard>

              <FormCard title="Numeri Seriali" icon="Hash" description="Confronto rapido S/N rientrato, smontato e montato" gridClassName="grid-cols-1 sm:grid-cols-3">
                <TextField label="S/N Rientrato" name="snrientrato" value={formData.snrientrato} onChange={handleChange} placeholder="Numero seriale rientrato" icon="Hash" sublabel="opzionale" />
                <TextField label="S/N Smontato" name="snsmontato" value={formData.snsmontato} onChange={handleChange} placeholder="Numero seriale smontato" icon="Hash" sublabel="opzionale" />
                <TextField label="S/N Montato" name="snmontato" value={formData.snmontato} onChange={handleChange} placeholder="Numero seriale montato" icon="Hash" sublabel="opzionale" />
              </FormCard>

              <FormCard title="Note" icon="FileText" gridClassName="grid-cols-1">
                <TextField label="Note" name="note" value={formData.note} onChange={handleChange} placeholder="Note aggiuntive sull'intervento" icon="FileText" rows={4} />
              </FormCard>

              <div className="rounded-2xl p-4 bg-primary/5 backdrop-blur-md border border-primary/20 flex flex-wrap gap-x-5 gap-y-2">
                <span className="w-full text-[9px] font-black uppercase tracking-widest text-muted-foreground/40">Riepilogo intervento</span>
                {[['Tipo', formData.tipointervento], ['Macchina', formData.n_macchina], ['Linea', formData.linea], ['Colata', formData.colata], ['Posizione', formData.posizione], ['Causa freno', formData.causa_freno ? 'SI' : 'NO']].map(([label, value]) => (
                  <span key={label} className="text-[11px]">
                    <span className="text-muted-foreground/40 font-bold uppercase text-[9px] tracking-widest">{label}: </span>
                    <span className="font-black text-foreground/70">{value || '---'}</span>
                  </span>
                ))}
              </div>
            </div>
          </form>

          {/* Footer */}
          <div className="shrink-0 px-5 md:px-10 py-5 md:py-6 bg-muted/30 backdrop-blur-sm border-t border-border/40 flex flex-col sm:flex-row gap-3 relative z-10">
            <div className="hidden sm:flex items-center gap-2 text-muted-foreground/30 mr-auto">
              <Icon name="Info" size={12} />
              <span className="text-[9px] font-black uppercase tracking-[.2em]">{campiCompilati} campi compilati</span>
            </div>
            <button type="button" onClick={handleClose} disabled={loading} className="flex-1 sm:flex-none sm:w-36 py-3 md:py-3.5 rounded-2xl bg-muted hover:bg-red-500/10 hover:text-red-500 border border-border/40 hover:border-red-500/20 text-foreground text-[11px] font-black uppercase tracking-[.2em] transition-all disabled:opacity-50">
              Annulla
            </button>
            <button type="submit" form="raddrizzatrice-form" disabled={loading} className="flex-1 sm:flex-2 py-3 md:py-3.5 rounded-2xl bg-primary text-primary-foreground text-[11px] font-black uppercase tracking-[.2em] shadow-lg shadow-primary/20 hover:opacity-90 transition-all disabled:opacity-50 flex items-center justify-center gap-2">
              {loading ? <><Icon name="Loader2" size={15} className="animate-spin" />Salvataggio...</> : <><Icon name="Save" size={15} />Salva Intervento</>}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default NewInsertRaddrizzatrice;